import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section } from "../components/Section.js";
import { track } from "../lib/analytics.js";

/**
 * 404 page.
 *
 * SPAs can't actually return a 404 status code from the client, but we can
 * still:
 *   - tell humans what happened in plain English,
 *   - hand them six high-intent links so the bounce rate stays low,
 *   - tell crawlers not to index the page (`noindex`),
 *   - log the bad path so we can see which broken links are hot in analytics.
 */

type Tile = { label: string; to: string; blurb: string };

const TILES: Tile[] = [
  { label: "Games", to: "/games", blurb: "The catalogue — three live originals." },
  { label: "Integration", to: "/integration", blurb: "How an operator wires us in." },
  { label: "Sandbox", to: "/sandbox", blurb: "Self-serve sandbox key in 2 minutes." },
  { label: "Pricing", to: "/#pricing", blurb: "10% of GGR. No setup fees." },
  { label: "FAQ", to: "/faq", blurb: "Buyer questions, answered straight." },
  { label: "Contact", to: "/contact", blurb: "Talk to us about a pilot." },
];

export function NotFound() {
  useEffect(() => {
    // Capture the path the visitor was actually trying to reach. SSR-safe:
    // window is always defined inside useEffect.
    track("not_found_view", { from: window.location.pathname });
  }, []);

  return (
    <>
      <Seo title="Page not found · Vault Breaker Studios" path="/404" />
      {/* `Seo` doesn't expose a noindex prop today, so layer one in directly.
          Helmet de-dupes meta tags by name, so this won't conflict with the
          site-wide head. Per-page only — never apply globally. */}
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Section style={{ paddingTop: 96, paddingBottom: 64, textAlign: "center" }}>
        <span
          className="label"
          style={{ display: "block", color: "var(--fg-3)", marginBottom: 14 }}
        >
          404 · Page not found
        </span>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 80,
            lineHeight: 1,
            color: "var(--gold-bright)",
            margin: 0,
            letterSpacing: "0.01em",
          }}
        >
          THIS PAGE DOESN'T EXIST.
        </h1>
        <p
          style={{
            color: "var(--fg-2)",
            marginTop: 16,
            marginBottom: 0,
            fontSize: 16,
            lineHeight: 1.55,
            maxWidth: 560,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          We may have moved it. Here are some places worth a click:
        </p>
      </Section>

      <Section style={{ paddingTop: 8, paddingBottom: 32 }}>
        <div
          style={{
            display: "grid",
            gap: 14,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {TILES.map((tile) => (
            <Link
              key={tile.to}
              to={tile.to}
              style={{ textDecoration: "none" }}
            >
              <Card
                className="vb-tile"
                style={{ padding: "18px 18px", height: "100%" }}
              >
                <span
                  style={{
                    display: "block",
                    fontFamily: "var(--font-display)",
                    fontSize: 18,
                    color: "var(--fg-1)",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  {tile.label}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 13,
                    color: "var(--fg-3)",
                    lineHeight: 1.5,
                  }}
                >
                  {tile.blurb}
                </span>
              </Card>
            </Link>
          ))}
        </div>
      </Section>

      <Section
        tight
        style={{
          paddingTop: 8,
          paddingBottom: 64,
          textAlign: "center",
        }}
      >
        <p
          style={{
            color: "var(--fg-3)",
            fontSize: 13,
            margin: 0,
            lineHeight: 1.6,
          }}
        >
          If you came from an external link, please let us know:{" "}
          <a
            href="mailto:hello@vaultbreaker.dev?subject=Broken%20link"
            style={{ color: "var(--fg-2)", textDecoration: "none" }}
          >
            hello@vaultbreaker.dev
          </a>
          .
        </p>
      </Section>
    </>
  );
}
